import { useEffect, useMemo, useRef, useState } from "react";
import { getMaterialJob, saveMaterialDocument } from "../../api/materialParsing";
import { createMaterialDraftSession, materialDraftOwner } from "../../features/document-processing/materialDraft";

export default function useMaterialDraft(authToken, onJob) {
  const onJobRef = useRef({ authToken, callback: onJob }); onJobRef.current = { authToken, callback: onJob };
  const session = useMemo(() => createMaterialDraftSession({
    owner: materialDraftOwner(authToken),
    storage: { getItem: (key) => window.localStorage.getItem(key), setItem: (key, value) => window.localStorage.setItem(key, value), removeItem: (key) => window.localStorage.removeItem(key), keys: () => Object.keys(window.localStorage) },
    save: (id, document, revision, options) => saveMaterialDocument(id, document, revision, { authToken, ...options }),
    fetchJob: (id) => getMaterialJob(id, { authToken }),
    onJob: (job) => { if (onJobRef.current.authToken === authToken) onJobRef.current.callback(job); },
  }), [authToken]);
  const [snapshot, setSnapshot] = useState(() => session.snapshot());
  useEffect(() => {
    session.activate(); setSnapshot(session.snapshot());
    const unsubscribe = session.subscribe(setSnapshot);
    const leave = () => { session.flush({ keepalive: true }).catch(() => {}); };
    const hidden = () => { if (window.document.visibilityState === "hidden") leave(); };
    const retry = () => { session.retry().catch(() => {}); };
    window.addEventListener("pagehide", leave);
    window.addEventListener("online", retry);
    window.document.addEventListener("visibilitychange", hidden);
    return () => {
      // Local backup is synchronous. keepalive is best-effort when a tab closes;
      // normal debounced writes are the durable server checkpoint.
      leave(); unsubscribe(); session.dispose();
      window.removeEventListener("pagehide", leave);
      window.removeEventListener("online", retry);
      window.document.removeEventListener("visibilitychange", hidden);
    };
  }, [session]);
  return { ...snapshot, session };
}
